import React from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Science as ExperimentIcon,
  AutoAwesome as AIIcon,
  TrendingDown,
  Warning,
  Analytics,
  CheckCircle,
  TrendingUp
} from '@mui/icons-material';
import { colors } from '../typography';
import { createFilterDropdown } from '../../components/ProblemsTable/filterConfig';
import AISummaryChip from '../../components/ProblemsTable/AISummaryChip';

// Severity color mapping
const getSeverityColor = (severity) => {
  if (!severity) return colors.text.secondary;
  const severityNum = parseFloat(severity);
  if (severityNum >= 0.8) return colors.status.error;
  if (severityNum >= 0.5) return colors.status.warning;
  return colors.status.success;
};

// Days active color coding
const getDaysActiveColor = (days) => {
  if (!days) return colors.text.secondary;
  if (days > 30) return colors.status.error;
  if (days > 14) return colors.status.warning;
  return colors.status.success;
};

// Estimated loss formatting
const formatLoss = (loss) => {
  if (!loss || loss === 0) return '€0';
  return `€${Math.abs(parseFloat(loss)).toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

// Get icon component by name (updated for minimal design)
const getIconComponent = (iconName) => {
  const iconMap = {
    'TrendingDown': TrendingDown,
    'TrendingUp': TrendingUp,
    'Analytics': Analytics,
    'CheckCircle': CheckCircle,
    'Warning': Warning
  };
  return iconMap[iconName] || Analytics;
};

// Get minimal icon for metric type (improved icons)
const getMinimalIcon = (type, icon) => {
  // Las métricas del orquestador no traen `type`, así que caían en el `return '↓'` final y una
  // GANANCIA se pintaba con flecha hacia abajo. Se resuelven por el icono que manda el backend.
  if (icon === 'TrendingUp') return '↑';
  if (icon === 'TrendingDown') return '↓';
  if (type === 'current' && icon === 'arrow_down') return '↓';
  if (type === 'current' && icon === 'half_circle') return '↓';
  if (type === 'benchmark' && icon === 'triangle_up') return '↑';
  if (type === 'market' && icon === 'triangle_up') return '↑';
  // Fallback for old icons
  if (type === 'current') return '↓';
  if (type === 'benchmark') return '↑';
  if (type === 'market') return '↑';
  return '↓';
};

// Get warm color for metric status
const getWarmColor = (status) => {
  switch (status) {
    case 'danger': return '#D32F2F'; // Darker warm red
    case 'warning': return '#F57C00'; // Darker warm amber
    case 'good': return '#388E3C'; // Darker mint green
    case 'normal': 
    default: return '#757575'; // Darker warm gray
  }
};

const renderRoutes = (routes = []) => {
  const normalized = Array.isArray(routes) ? routes : [];
  const names = normalized
    .map((route) => {
      if (route && typeof route === 'object') {
        return route.name || route.code || route.label || route.id;
      }
      return route;
    })
    .filter(Boolean)
    .map((name) => String(name));

  if (names.length === 0) {
    return (
      <Typography sx={{ 
        fontSize: '12px', 
        color: '#787774',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
      }}>
        -
      </Typography>
    );
  }

  const displayText = names.slice(0, 2).join(', ');
  const tooltipText = names.join(', ');
  const hasOverflow = names.length > 2;

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Typography sx={{ 
        fontSize: '12px', 
        color: '#37352f',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        fontWeight: 400,
        letterSpacing: '-0.01em',
        textAlign: 'center'
      }}>
        {hasOverflow ? `${displayText}…` : displayText}
      </Typography>
    </Tooltip>
  );
};

// Create world-class tooltip content
const createTooltipContent = (metrics, t, context = null) => {
  // Menos de 3 métricas = contexto del orquestador (1 métrica + headline + actionHint). El
  // tooltip de abajo desestructura `[current, benchmark, market]` del detector estacional, así
  // que aquí se pinta uno simple en vez de devolver null y dejar la fila sin explicación.
  if (!metrics || metrics.length < 3) {
    if (!context || (!context.headline && !(metrics || []).length)) return null;
    return (
      <Box sx={{ padding: '12px', minWidth: '240px', maxWidth: 360, fontFamily: 'SF Pro, Inter, system-ui', fontSize: '11px' }}>
        {context.headline && (
          <Typography sx={{ fontSize: '11px', color: '#2D3748', lineHeight: 1.45, fontWeight: 600 }}>
            {context.headline}
          </Typography>
        )}
        {(metrics || []).map((m, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
            <Typography sx={{ fontSize: '11px', color: '#718096' }}>{m.label}</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: m.status === 'good' ? '#2e7d32' : '#2D3748' }}>
              {m.status === 'good' ? '+' : ''}{typeof m.value === 'number' ? m.value.toLocaleString('es-ES') : m.value} {m.unit || ''}
            </Typography>
          </Box>
        ))}
        {context.actionHint && (
          <Typography sx={{ fontSize: '11px', color: '#2e7d32', fontWeight: 600, mt: 1.25 }}>
            {context.actionHint}
          </Typography>
        )}
      </Box>
    );
  }

  const [current, benchmark, market] = metrics;

  return (
    <Box sx={{ 
      padding: '12px',
      minWidth: '240px',
      fontFamily: 'SF Pro, Inter, system-ui',
      fontSize: '11px'
    }}>
      {/* Title */}
      <Typography sx={{ 
        fontSize: '12px', 
        fontWeight: 600,
        color: '#2D3748',
        mb: 1.5,
        letterSpacing: '-0.01em'
      }}>
        {t('product_performance')}
      </Typography>
      
      {/* Current Sales */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Typography sx={{ 
          fontSize: '14px', 
          color: getWarmColor(current.status),
          mt: -0.2
        }}>
          ↓
        </Typography>
        <Box>
          <Typography sx={{ 
            fontSize: '11px', 
            fontWeight: 500,
            color: '#2D3748',
            lineHeight: 1.2
          }}>
            {t(current.detail?.description || 'seasonal_underperformance.current_description')}
          </Typography>
          <Typography sx={{ 
            fontSize: '11px',
            color: '#718096',
            lineHeight: 1.2
          }}>
            {formatCompactNumber(current.value)} {t('units')} ({t(current.detail?.period || 'seasonal_underperformance.current_period')})
          </Typography>
        </Box>
      </Box>
      
      {/* Category Benchmark */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Typography sx={{ 
          fontSize: '14px', 
          color: '#388E3C',
          mt: -0.2
        }}>
          ↑
        </Typography>
        <Box>
          <Typography sx={{ 
            fontSize: '11px', 
            fontWeight: 500,
            color: '#2D3748',
            lineHeight: 1.2
          }}>
            {t(benchmark.detail?.description || 'seasonal_underperformance.benchmark_description')}
          </Typography>
          <Typography sx={{ 
            fontSize: '11px',
            color: '#718096',
            lineHeight: 1.2
          }}>
            {formatCompactNumber(benchmark.value)} {t('units_in')} {benchmark.detail?.category || 'unknown_category'} ({t('seasonal_underperformance.benchmark_period')})
          </Typography>
          {benchmark.detail?.sector && (
            <Typography sx={{ 
              fontSize: '10px',
              color: '#A0AEC0',
              lineHeight: 1.2
            }}>
              {t('sector')}: {benchmark.detail.sector}
            </Typography>
          )}
        </Box>
      </Box>
      
      {/* Market Activity */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ 
          fontSize: '14px', 
          color: '#388E3C',
          mt: -0.2
        }}>
          ↑
        </Typography>
        <Box>
          <Typography sx={{ 
            fontSize: '11px', 
            fontWeight: 500,
            color: '#2D3748',
            lineHeight: 1.2
          }}>
            {t(market.detail?.description || 'seasonal_underperformance.market_description')}
          </Typography>
          <Typography sx={{ 
            fontSize: '11px',
            color: '#718096',
            lineHeight: 1.2
          }}>
            {formatCompactNumber(market.value)} {t('total_sales')} ({t(market.detail?.period || 'seasonal_underperformance.market_period')})
          </Typography>
          {(market.detail?.cluster || market.detail?.location) && (
            <Typography sx={{ 
              fontSize: '10px',
              color: '#A0AEC0',
              lineHeight: 1.2,
              mt: 0.2
            }}>
              {market.detail?.cluster || (market.detail?.location !== 'sales_point' ? market.detail?.location : '')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Get status color for metrics
const getStatusColor = (status) => {
  switch (status) {
    case 'danger': return colors.status.error;
    case 'warning': return colors.status.warning;
    case 'good': return colors.status.success;
    case 'normal': 
    default: return colors.text.secondary;
  }
};

// Truncate text function for product names
const truncateText = (text, maxLength = 26) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Format numbers for compact display (e.g., 42100 -> 42.1K)
const formatCompactNumber = (value) => {
  if (!value || isNaN(value)) return value;
  
  const num = parseFloat(value);
  
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  
  return num.toString();
};

export const getAggregatedColumns = (onAcceptAll, t) => {
  const columns = [
    {
      title: t('groupingLabel', { defaultValue: 'Grouping' }),
      dataIndex: 'groupingLabel',
      key: 'groupingLabel',
      width: 160,
      align: 'left',
      render: (text) => (
        <span style={{
          fontSize: '12px',
          fontWeight: 400,
          color: '#37352f',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          letterSpacing: '-0.01em'
        }}>
          {text || '-'}
        </span>
      )
    },
    {
      title: t('aiInsight', { defaultValue: 'AI Insight' }),
      dataIndex: 'breakdown',
      key: 'aiInsight',
      width: 185,
      align: 'left',
      render: (breakdown, record) => (
        <AISummaryChip
          breakdown={breakdown}
          totalCost={record.totalOpportunityCost}
          machineCount={record.machineCount}
          experimentCount={record.experimentCount}
          groupLabel={record.groupingLabel}
        />
      )
    },
    {
      title: t('totalOpportunityCost', { defaultValue: 'Total Opp. Cost' }),
      dataIndex: 'totalOpportunityCost',
      key: 'totalOpportunityCost',
      width: 120,
      align: 'center',
      sorter: true,
      render: (value) => (
        <span style={{
          fontSize: '13px',
          fontWeight: 500,
          color: '#d9534f',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          letterSpacing: '-0.01em'
        }}>
          {formatLoss(value)}
        </span>
      )
    },
    {
      title: t('machines', { defaultValue: 'Machines' }),
      dataIndex: 'machineCount',
      key: 'machineCount',
      width: 80,
      align: 'center',
      render: (count) => (
        <span style={{
          fontSize: '13px',
          fontWeight: 400,
          color: '#37352f',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          letterSpacing: '-0.01em'
        }}>
          {count || 0}
        </span>
      )
    },
    {
      title: t('experiments', { defaultValue: 'Experiments' }),
      dataIndex: 'experimentCount',
      key: 'experimentCount',
      width: 90,
      align: 'center',
      render: (count) => (
        <span style={{
          fontSize: '13px',
          fontWeight: 400,
          color: '#37352f',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          letterSpacing: '-0.01em'
        }}>
          {count || 0}
        </span>
      )
    },
    {
      title: t('acciones'),
      key: 'actions',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={t('acceptAll', { defaultValue: 'Accept All' })}>
          <span
            onClick={() => onAcceptAll(record.experimentIds, record.groupingLabel)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#448361',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              letterSpacing: '-0.01em',
              cursor: 'pointer',
              borderRadius: '4px',
              backgroundColor: 'rgba(68, 131, 97, 0.08)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(68, 131, 97, 0.15)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(68, 131, 97, 0.08)'}
          >
            <CheckCircle sx={{ fontSize: '14px' }} />
            {t('acceptAll', { defaultValue: 'Accept All' })}
          </span>
        </Tooltip>
      )
    }
  ];

  return columns;
};

export const createProblemsColumns = (
  filteredInfo,
  sortedInfo,
  filterValues,
  filtersLoading,
  navigate,
  onOpenAIAnalysisPanel,
  t
) => {
  const columns = [
    {
      title: t('machineId'),
      dataIndex: 'machineId', 
      key: 'machineId',
      width: 75,
      align: 'center',
      sorter: true,
      sortOrder: sortedInfo?.field === 'machineId' ? sortedInfo.order : null,
      render: (machineId, record) => {
        const handleMachineIdClick = () => {
          navigate('/machines', {
            state: {
              defaultTab: 'planograma',
              machineStoreId: machineId,
              filters: {
                group: record.group,
                location: record.location
              }
            }
          });
        };

        return (
          <Tooltip title={`${record.group || ''} - ${record.location || ''}`} arrow placement="top">
            <Typography 
              component="span"
              onClick={handleMachineIdClick}
              sx={{
                fontSize: '12px',
                color: '#0969da',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                textAlign: 'center',
                '&:hover': {
                  color: '#0550ae'
                }
              }}
            >
              {machineId || '-'}
            </Typography>
          </Tooltip>
        );
      },
      filterDropdown: createFilterDropdown('machineId', filterValues, t('machineId'), filtersLoading, t),
      filteredValue: filteredInfo.machineId || null
    },
    {
      title: t('categoria'),
      dataIndex: 'product',
      key: 'category',
      width: 100,
      align: 'center',
      sorter: true,
      sortOrder: sortedInfo?.field === 'product' ? sortedInfo.order : null,
      render: (product) => (
        <Typography sx={{ 
          fontSize: '12px', 
          color: '#787774',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          fontWeight: 400,
          letterSpacing: '-0.01em',
          textAlign: 'center'
        }}>
          {product?.category || t('desconocido')}
        </Typography>
      ),
      filterDropdown: createFilterDropdown('productCategory', filterValues, t('product_category_filter'), filtersLoading, t),
      filteredValue: filteredInfo.category || null
    },
    {
      title: t('producto'),
      dataIndex: 'productName',
      key: 'product',
      width: 180,
      align: 'center',
      sorter: true,
      sortOrder: sortedInfo?.field === 'productName' ? sortedInfo.order : null,
      render: (productName) => {
        const displayName = productName || t('unknown_product');
        const truncated = truncateText(displayName);
        
        const content = (
          <Typography sx={{ 
            fontSize: '12px', 
            fontWeight: 500,
            color: '#37352f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            letterSpacing: '-0.01em',
            cursor: 'help',
            textAlign: 'center'
          }}>
            {truncated}
          </Typography>
        );
        
        // Always show tooltip for consistency
        return (
          <Tooltip title={displayName} enterDelay={500} arrow>
            {content}
          </Tooltip>
        );
      },
      filterDropdown: createFilterDropdown('product', filterValues, t('producto'), filtersLoading, t),
      filteredValue: filteredInfo.product || null
    },
    {
      title: t('tipo'),
      dataIndex: 'problemType',
      key: 'type',
      width: 100,
      align: 'center',
      sorter: true,
      sortOrder: sortedInfo?.field === 'problemType' ? sortedInfo.order : null,
      render: (problemType, record) => {
        return (
          <Chip
            label={problemType || t('desconocido')}
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontSize: '11px',
              height: '24px',
              backgroundColor: '#f8f9fa',
              color: '#37352f',
              border: `1px solid #e2e8f0`,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              letterSpacing: '-0.01em',
              cursor: 'default'
            }}
          />
        );
      },
      filterDropdown: createFilterDropdown('problemType', filterValues, t('problem_type'), filtersLoading, t),
      filteredValue: filteredInfo.problemType || null
    },
    {
      title: t('totalLost'),
      dataIndex: 'estimatedLoss',
      key: 'estimatedLoss',
      width: 160,
      align: 'center',
      sorter: true,
      sortOrder: sortedInfo?.field === 'estimatedLoss' ? sortedInfo.order : null,
      render: (loss, record) => {
        // El backend declara el signo en `context.metrics[0].status`: 'good' = GANANCIA que se
        // captura actuando (orquestador), no una pérdida ya incurrida. Sin esto las subidas de
        // precio se pintaban en rojo como si fueran dinero perdido.
        const isGain = record?.context?.metrics?.[0]?.status === 'good';
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: isGain ? '#2e7d32' : '#E57373',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              letterSpacing: '-0.01em'
            }}>
              {isGain ? '+' : ''}{formatLoss(loss)}
            </Typography>
          </Box>
        );
      }
    },
    {
      title: t('contexto'),
      dataIndex: 'context',
      key: 'context',
      width: 180,
      align: 'center',
      render: (context, record) => {
        if (!context) {
          return (
            <Typography sx={{ 
              fontSize: '12px', 
              color: '#787774',
              fontStyle: 'italic',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontWeight: 400,
              letterSpacing: '-0.01em',
              textAlign: 'center'
            }}>
              {t('sin_contexto_disponible')}
            </Typography>
          );
        }
        
        return (
          <Tooltip
            title={createTooltipContent(context.metrics, t, context)}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#FFFFFF',
                  color: '#2D3748',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  fontSize: '11px',
                  maxWidth: 'none'
                }
              },
              arrow: {
                sx: {
                  color: '#FFFFFF',
                  '&:before': {
                    border: '1px solid #E2E8F0'
                  }
                }
              }
            }}
          >
            <Box sx={{ minWidth: 140, display: 'flex', gap: 2, alignItems: 'center', cursor: 'pointer', justifyContent: 'center' }}>
              {/* Hasta 3 métricas. Antes exigía EXACTAMENTE 3 o más, y las oportunidades del
                  orquestador mandan 1 ("Margen bruto incremental"), así que caían todas en
                  "Sin métricas disponibles" pese a venir con su dato en el payload. */}
              {context.metrics && context.metrics.length > 0 ? (
                context.metrics.slice(0, 3).map((metric, index) => {
                  // Override colors: current keeps original logic, benchmark and market = green
                  let displayColor;
                  if (metric.type === 'current') {
                    displayColor = getWarmColor(metric.status); // Keep original color logic for current
                  } else {
                    displayColor = '#388E3C'; // Darker green for benchmark and market
                  }
                  
                  const minimalIcon = getMinimalIcon(metric.type, metric.icon);
                  
                  return (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.3,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      <span style={{ 
                        color: displayColor, 
                        fontSize: '12px',
                        fontFamily: 'SF Pro, Inter, system-ui',
                        letterSpacing: '-0.01em'
                      }}>
                        {minimalIcon}
                      </span>
                      <Typography sx={{ 
                        fontSize: '12px', 
                        color: displayColor,
                        fontWeight: metric.type === 'current' ? 500 : 400,
                        fontFamily: 'SF Pro, Inter, system-ui',
                        letterSpacing: '-0.01em'
                      }}>
                        {formatCompactNumber(metric.value)}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '6px', 
                        color: '#9E9E9E',
                        fontFamily: 'SF Pro, Inter, system-ui'
                      }}>
                        ●
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '10px', 
                        color: '#9E9E9E',
                        fontWeight: 500,
                        fontFamily: 'SF Pro, Inter, system-ui',
                        letterSpacing: '-0.01em'
                      }}>
                        {metric.label || 'N/A'}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                /* Fallback for missing metrics */
                <Typography sx={{ 
                  fontSize: '11px', 
                  color: '#787774',
                  fontStyle: 'italic',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  textAlign: 'center'
                }}>
                  {t('sin_metricas_disponibles')}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      }
    },
    {
      title: t('acciones'),
      key: 'actions',
      fixed: 'right',
      width: 50,
      render: (_, record) => (
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Tooltip title={t('ai_analysis_create_experiment') || 'AI Analysis & Create Experiment'}>
            <IconButton
              size="small"
              onClick={() => onOpenAIAnalysisPanel(record.id)}
              sx={{
                padding: '4px',
                backgroundColor: colors.accent.purple + '10',
                '&:hover': {
                  backgroundColor: colors.accent.purple + '20'
                }
              }}
            >
              <AIIcon sx={{ 
                fontSize: '16px',
                color: colors.accent.purple
              }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return columns;
};
